/**
 * Dashboard — Admin stats overview
 */

import { useEffect } from 'react';
import { useAdminStore } from '../../../store/adminStore';

export function Dashboard() {
  const { stats, isLoading, error, loadStats } = useAdminStore();

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  if (isLoading && !stats) {
    return <div style={styles.loading}>載入中...</div>;
  }

  if (error) {
    return <div style={styles.error}>❌ {error}</div>;
  }

  return (
    <div>
      <h1 style={styles.title}>📊 管理總覽</h1>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>🟢</div>
          <div style={styles.statValue}>{stats?.online_count ?? 0}</div>
          <div style={styles.statLabel}>線上玩家</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>⚔️</div>
          <div style={styles.statValue}>{stats?.total_games ?? 0}</div>
          <div style={styles.statLabel}>總對戰數</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>👥</div>
          <div style={styles.statValue}>{stats?.total_players ?? 0}</div>
          <div style={styles.statLabel}>註冊玩家</div>
        </div>
      </div>

      {/* Top Players */}
      <h2 style={styles.sectionTitle}>🏆 MMR 排行榜</h2>
      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ ...styles.tableCell, flex: 0.5 }}>#</span>
          <span style={{ ...styles.tableCell, flex: 2 }}>玩家</span>
          <span style={styles.tableCell}>MMR</span>
          <span style={styles.tableCell}>等級</span>
          <span style={styles.tableCell}>勝</span>
          <span style={styles.tableCell}>負</span>
          <span style={styles.tableCell}>勝率</span>
        </div>
        {(stats?.top_players ?? []).map((player, i) => {
          const totalGames = player.wins + player.losses;
          const winRate = totalGames > 0 ? (player.wins / totalGames * 100).toFixed(1) : '0.0';
          return (
            <div key={player.id} style={styles.tableRow}>
              <span style={{ ...styles.tableCell, flex: 0.5, fontWeight: 'bold' }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : String(i + 1)}
              </span>
              <span style={{ ...styles.tableCell, flex: 2 }}>{player.username}</span>
              <span style={{ ...styles.tableCell, color: '#ffcc00' }}>{player.mmr}</span>
              <span style={styles.tableCell}>{player.level}</span>
              <span style={{ ...styles.tableCell, color: '#44ff44' }}>{player.wins}</span>
              <span style={{ ...styles.tableCell, color: '#ff4444' }}>{player.losses}</span>
              <span style={styles.tableCell}>{winRate}%</span>
            </div>
          );
        })}
        {(stats?.top_players ?? []).length === 0 && (
          <div style={styles.emptyRow}>暫無玩家資料</div>
        )}
      </div>

      <button
        style={styles.refreshButton}
        onClick={() => { void loadStats(); }}
        disabled={isLoading}
      >
        🔄 重新整理
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: '24px', margin: '0 0 24px 0' },
  sectionTitle: { fontSize: '18px', margin: '32px 0 16px 0' },
  loading: { padding: '40px', textAlign: 'center', color: '#888888' },
  error: { padding: '20px', color: '#ff4444' },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  statCard: {
    backgroundColor: '#252545',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center',
  },
  statIcon: { fontSize: '32px', marginBottom: '8px' },
  statValue: { fontSize: '36px', fontWeight: 'bold', color: '#ffffff' },
  statLabel: { fontSize: '14px', color: '#888888', marginTop: '4px' },
  table: {
    backgroundColor: '#252545',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    padding: '12px 16px',
    backgroundColor: '#1e1e3e',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#888888',
    textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex',
    padding: '10px 16px',
    borderTop: '1px solid #3a3a5a',
    fontSize: '14px',
  },
  tableCell: {
    flex: 1,
  },
  emptyRow: {
    padding: '20px',
    textAlign: 'center',
    color: '#666666',
    fontSize: '14px',
  },
  refreshButton: {
    marginTop: '24px',
    padding: '10px 20px',
    backgroundColor: '#3a5a8a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
};
